import { createAppConfig } from './createAppConfig';
import { envOrEmpty, optionalEnv, requireEnv } from './requireEnv';
import type { AppConfig, CreateAppConfigInput } from './types';

/** Standard Vite env keys every microapp must define in `.env`. */
export interface PlatformViteEnv {
  VITE_ACCOUNT_API_URL?: string;
  VITE_ACCOUNT_DOMAIN?: string;
  VITE_CLIENT_ID?: string;
  VITE_MAIN_API_BASE_URL?: string;
  VITE_APP_ORIGIN?: string;
  VITE_LOGIN_REDIRECT_URI?: string;
  VITE_ACCOUNT_LOGIN_PATH?: string;
}

export interface CreateAppConfigFromEnvOptions
  extends Omit<
    CreateAppConfigInput,
    | 'accountApiUrl'
    | 'accountDomain'
    | 'clientId'
    | 'mainApiUrl'
    | 'devAppOrigin'
    | 'loginRedirectUri'
    | 'accountLoginPath'
  > {
  /** When true, `VITE_MAIN_API_BASE_URL` may be `""` (vite proxy). Default false. */
  allowEmptyMainApiUrl?: boolean;
}

/**
 * Build platform config exclusively from Vite env — no baked-in production/dev URLs.
 */
export function createAppConfigFromEnv(
  env: Record<string, string | undefined>,
  options: CreateAppConfigFromEnvOptions,
): AppConfig {
  const { allowEmptyMainApiUrl = false, ...rest } = options;

  const mainApiUrl = allowEmptyMainApiUrl
    ? envOrEmpty(env.VITE_MAIN_API_BASE_URL, 'VITE_MAIN_API_BASE_URL')
    : requireEnv(env.VITE_MAIN_API_BASE_URL, 'VITE_MAIN_API_BASE_URL');

  const configInput: CreateAppConfigInput = {
    ...rest,
    clientId: requireEnv(env.VITE_CLIENT_ID, 'VITE_CLIENT_ID'),
    accountApiUrl: requireEnv(env.VITE_ACCOUNT_API_URL, 'VITE_ACCOUNT_API_URL'),
    accountDomain: requireEnv(env.VITE_ACCOUNT_DOMAIN, 'VITE_ACCOUNT_DOMAIN'),
    mainApiUrl,
  };

  const devAppOrigin = optionalEnv(env.VITE_APP_ORIGIN);
  if (devAppOrigin) configInput.devAppOrigin = devAppOrigin;

  const loginRedirectUri = optionalEnv(env.VITE_LOGIN_REDIRECT_URI);
  if (loginRedirectUri) configInput.loginRedirectUri = loginRedirectUri;

  const accountLoginPath = optionalEnv(env.VITE_ACCOUNT_LOGIN_PATH);
  if (accountLoginPath) configInput.accountLoginPath = accountLoginPath;

  return createAppConfig(configInput);
}
